<!-- 
/**
 * Product search component.
 * 
 * @since 2.0.0
 * @author Fahri Rusliyadi <fahri.rusliyadi@gmail.com>
 */
-->

<template>
    <div class="products-search">
        <form>
            <div class="form-group">
                <div class="input-group">
                    <input v-model="q" type="search" class="form-control" placeholder="Search for...">
                    <span class="input-group-append">
                        <button class="btn btn-secondary" v-on:click.prevent="debouncedSearch">Go!</button>
                    </span>
                </div>
            </div>
        </form>

        <products-search-results v-if="searchResults.length" v-bind:columns="columns" v-bind:items="searchResults"></products-search-results>
    </div>
</template>

<script>
    /**
     * Column data.
     * 
     * @since 2.0.0
     * @typedef {Object} ProductSearchResultColumn
     * @property {String} title - Column title.
     * @property {String} component - Column item Vue component.
     */

    const _ = require('lodash');
    const axios = require('axios');

    export default {
        /**
         * Custom attributes. 
         * 
         * @since 2.0.0
         * @type {String[]}
         */
        props: [
            /**
             * Search action URL.
             * 
             * @since 2.0.0
             * @type {String}
             */
            'action'
        ],

        /**
         * Component data.
         * 
         * @since 2.0.0
         * @type {Function}
         */
        data: () => ({
            /**
             * Search keywords.
             * 
             * @since 2.0.0
             * @type {String}
             */
            q: '',

            /**
             * Columns data.
             * 
             * @since 2.0.0
             * @type {Object.<String, ProductSearchResultColumn>} - {
             *      {String} Column ID: ProductSearchResultColumn
             *  }
             */
            columns: {},

            /**
             * Products.
             * 
             * @since 2.0.0
             * @type {Object.<Number, Object>} - {
             *      {Number} Product ID: {Object} Product data.
             *  }
             */
            items: {},

            /**
             * Cached search results.
             * 
             * @since 2.0.0
             * @type {Object.<String, Number[]>} - {
             *      {String} Search keywords: {Array} [
             *          {Number} Product ID.
             *      ]
             *  }
             */
            cachedSearches: {}
        }),

        /**
         * Computed properties.
         * 
         * @since 2.0.0
         * @type {Object}
         */
        computed: {
            /**
             * Sanitized keywords.
             * 
             * Trimmed and converted to lowercase.
             * 
             * @since 2.0.0
             * @returns {String}
             */
            sanitizedQ: function() {
                return this.q.trim().toLowerCase();
            },

            /**
             * Search result items.
             * 
             * @since 2.0.0
             * @returns {Object[]} - [
             *      {Object} Product data.
             *  ]
             */
            searchResults: function() {
                return this.cachedSearches[this.sanitizedQ] 
                    ? this.cachedSearches[this.sanitizedQ].map(id => this.items[id])
                    : [];
            }
        },

        /**
         * Watch callbacks.
         * 
         * @since 2.0.0
         * @type {Object}
         */
        watch: {
            /**
             * Debounced search when `q` data changed.
             * 
             * @since 2.0.0
             */
            q: function() {                
                this.debouncedSearch();
            }
        },

        /**
         * Methods.
         * 
         * @since 2.0.0
         * @type {Object}
         */
        methods: {
            /**
             * Search for products.
             * 
             * @since 2.0.0
             */
            search: function() {
                this.setHashLocation();

                if (this.hasCachedSearchResults() || !this.sanitizedQ) {
                    return;
                }

                this.$root.$refs['progress-bar'].active = true;

                axios
                    .get(this.action, {
                        params: {
                            q: this.sanitizedQ
                        }
                    })
                    .then(response => {
                        this.columns = response.data.columns;

                        this.$set(this.cachedSearches, this.sanitizedQ, response.data.items.map(item => item.id));
                        response.data.items.forEach(item => this.$set(this.items, item.id, item));
                        this.$root.$refs['progress-bar'].active = false;
                    });
            },

            /**
             * Check if we have cached search results.
             * 
             * @since 2.0.0
             * @returns {Boolean}
             */
            hasCachedSearchResults: function() {
                return Boolean(
                    this.cachedSearches[this.sanitizedQ]
                );
            },

            /**
             * Update hash on the URL.
             * 
             * @since 2.0.0
             */
            setHashLocation: function() {
                if (this.sanitizedQ) {
                    location.hash = `#search=${this.q}`;
                } else if (location.hash) {
                    // Remove hash.
                    // https://stackoverflow.com/a/5298684/6759554
                    history.pushState({}, document.title, location.pathname + location.search);
                }
            },

            /**
             * Initialize `q` data using URL hash.
             * 
             * @since 2.0.0
             */
            initQ: function() {
                if (location.hash.indexOf('#search=') === 0) {
                    this.q = decodeURI(
                        location.hash.replace(/^#search=/, '')
                    );
                }
            }
        },

        /**
         * Initialize after the instance is created.
         * 
         * @since 2.0.0
         * @type {Function}
         */
        created: function () {
            this.debouncedSearch = _.debounce(this.search, 400);
            onpopstate = this.initQ;

            this.initQ();
        }
    }
</script>
